import * as React from "react";
import styled from "@emotion/styled";
import { trackEvent } from "contexts/mixpanel";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "features/auth";
import {
  ResourceCard,
  useResources,
  type ContentTag,
} from "features/gatedContent";
import image1 from "assets/images/about-artist-profiles/tippydreamer-middle-2.jpg";
import image2 from "assets/images/about-artist-profiles/ivan_casis_jr-middle-3.jpg";
import {
  AllCaps,
  BrushWrapper,
  Button,
  Container,
  CtaCentered,
  FlexRow,
  H1,
  H2,
  Link,
  LoadingSpinner,
  PageHeader,
  Stack,
  Text,
} from "atoms";

const filters: ContentTag[] = ["Artists", "Collectors"];

const trackSignUp = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign Up",
    ButtonContext: "Resources",
  });

const trackSignIn = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign In",
    ButtonContext: "Resources",
  });

function ResourceLibrary() {
  const { user } = useAuth();
  const location = useLocation();

  const { data: resources } = useResources();

  // /content/:contentSlug
  const { resourceSlug } = useParams<{ resourceSlug: string }>();
  // /content?tag=tagString or /content/:contentSlug?tag=tagString
  const [searchParams, setSearchParams] = useSearchParams();

  // Use :contentSlug from URL to find the permalink page's featured content, default to first
  // element in array as feature if we're on landing page (or if slug is incorrect)
  const currentFeature =
    resources?.find((resource) => resource.slug === resourceSlug) ??
    resources?.[0];

  // Get current tag filter, if it is set
  const currentTag = searchParams.get("tag");

  // Create a click handler to set the current tag filter, or remove/toggle it
  const createFilterHandler = (tag: ContentTag | null) => () => {
    setSearchParams((oldParams) => {
      if (!tag || currentTag === tag.toLowerCase()) {
        oldParams.delete("tag");
      } else {
        oldParams.set("tag", tag.toLowerCase());
      }
      return oldParams;
    });
  };

  // Use query parameter to filter which content pieces show in the grid
  const filteredContent = React.useMemo(
    () =>
      !currentTag
        ? resources
        : resources?.filter(
            (content) =>
              !!content.tags.find((tag) => tag.toLowerCase() === currentTag),
          ),
    [currentTag, resources],
  );

  if (!resources || !currentFeature || !filteredContent) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader width="lg" brush="none">
        <HeaderGrid>
          <Stack gap="sm">
            <H1 size="xxl">Art education for&nbsp;everyone</H1>
            <Stack>
              <Text size="xs">
                Artist or art lover&mdash;we got you. From building your first
                art collection to pricing your artwork, our free and
                easy-to-understand resources have been crafted for the art
                community by our team of expert educators.
              </Text>
              <Text size="xs">
                Access to our ever-growing resource library is free through your{" "}
                <AllCaps>Hug</AllCaps>&nbsp;account.
              </Text>
            </Stack>
          </Stack>

          <ResourceCard content={currentFeature} isFeatured />
        </HeaderGrid>
      </PageHeader>

      {!user && (
        <SignInCTA>
          <Container width="md">
            <FlexRow flexWrap="wrap" justifyContent="space-between">
              <Stack>
                <H2>Download for free now</H2>
                <Text>
                  Create a free <AllCaps>Hug</AllCaps> account to get full
                  access to all of our resources.
                  <br />
                  <strong>
                    Already have an account?{" "}
                    <Link
                      to="/sign-in"
                      state={{ from: location }}
                      onClick={trackSignIn}
                    >
                      Sign&nbsp;in&nbsp;to&nbsp;start&nbsp;downloading&nbsp;today.
                    </Link>
                  </strong>
                </Text>
              </Stack>
              <Link
                variant="secondary"
                to="/register"
                state={{ from: location }}
                onClick={trackSignUp}
              >
                Create an Account
              </Link>
            </FlexRow>
          </Container>
        </SignInCTA>
      )}
      <Main>
        <Container width="lg">
          <Stack gap="md">
            <FlexRow gap="10px" flexWrap="wrap">
              <H2 size="sm" style={{ margin: 0 }}>
                Explore resources for:
              </H2>
              <FlexRow gap="10px" flexWrap="nowrap">
                <Button
                  size="xs"
                  variant={!currentTag ? "primary" : "secondary"}
                  onClick={createFilterHandler(null)}
                >
                  All
                </Button>
                {filters.map((tag) => (
                  <Button
                    size="xs"
                    key={tag}
                    variant={
                      currentTag === tag.toLowerCase() ? "primary" : "secondary"
                    }
                    onClick={createFilterHandler(tag)}
                  >
                    {tag}
                  </Button>
                ))}
              </FlexRow>
            </FlexRow>

            <ContentGrid>
              {filteredContent.map((resource) => (
                <ResourceCard key={resource.slug} content={resource} />
              ))}
            </ContentGrid>
            <SCtaCentered bg="fg" image1={image1} image2={image2}>
              <H2>Brought to you by Hug&nbsp;Studios</H2>
              <Text>
                HUG Studios&apos; mission is to bring opportunities to artists
                so they can focus on the joy of creation. Programs are open to
                all artists with a HUG Artist Profile, and include open calls
                for exhibitions and merchandising, financial grants, guided
                programming, live workshops, and more.
              </Text>
              <Link to="/for-creators" variant="secondary" size="sm">
                Explore Hug Studios
              </Link>
            </SCtaCentered>
          </Stack>
        </Container>
      </Main>
    </div>
  );
}

const HeaderGrid = styled.div(({ theme }) => ({
  textAlign: "left",
  display: "flex",
  gap: "2rem",
  flexWrap: "wrap",
  justifyContent: "space-between",
  "&>*": { flex: "0 0 100%" },
  [theme.breakpoints.desktop]: {
    flexDirection: "row",
    alignItems: "center",
    "&>*:first-of-type": {
      flex: "1 0 230px",
    },
    "&>*:last-of-type": {
      flex: "1 0 450px",
    },
  },
  [theme.breakpoints.desktopLarge]: {
    "&>*:first-of-type": {
      flex: "0 0 450px",
    },
  },

  [theme.breakpoints.desktopLarge]: {
    "&>*": {
      maxWidth: 750,
    },
  },
}));

const Main = styled.div({
  padding: "3rem 0 5rem",
});

const ContentGrid = styled.div(({ theme }) => ({
  display: "grid",
  gap: theme.spacing.gap,
  [theme.breakpoints.xs]: {
    gridTemplateColumns: `repeat(2, calc(50% - (${theme.spacing.gap} * .5)))`,
  },
  [theme.breakpoints.desktop]: {
    gridTemplateColumns: `repeat(3, calc((100% / 3) - (${theme.spacing.gap} * .667)))`,
  },
}));

const SignInCTA = styled(BrushWrapper)(({ theme }) => ({
  padding: "2rem 0",
  color: theme.colors.fg,
}));
SignInCTA.defaultProps = {
  bg: "accent3",
  brush: "2",
};

const SCtaCentered = styled(CtaCentered)(({ theme }) => ({
  "&&": { margin: "5rem auto 2rem", position: "relative", zIndex: 4 },
  "a:link": { color: theme.colors.fg },
}));

export default ResourceLibrary;
